import * as React from 'react';
import { Search, X } from 'lucide-react';

export interface CommandItem {
  id: string;
  label: string;
  category: string;
  icon?: React.ReactNode;
  shortcut?: string;
  onSelect: () => void;
}

export function CommandDialog({
  open,
  onOpenChange,
  items = [],
  placeholder = 'Type a command or search...',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: CommandItem[];
  placeholder?: string;
}) {
  const [query, setQuery] = React.useState('');
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const filteredItems = React.useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return items;
    return items.filter(
      (item) =>
        item.label.toLowerCase().includes(q) || item.category.toLowerCase().includes(q)
    );
  }, [items, query]);

  // Group items by category
  const groups = React.useMemo(() => {
    const map = new Map<string, CommandItem[]>();
    for (const item of filteredItems) {
      const list = map.get(item.category) || [];
      list.push(item);
      map.set(item.category, list);
    }
    return Array.from(map.entries());
  }, [filteredItems]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onOpenChange(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % Math.max(1, filteredItems.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + filteredItems.length) % Math.max(1, filteredItems.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredItems[selectedIndex]) {
        filteredItems[selectedIndex].onSelect();
        onOpenChange(false);
      }
    }
  };

  if (!open) return null;

  return (
    <div
      className="shadcn-command-dialog"
      onClick={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className="shadcn-command-box" onKeyDown={handleKeyDown}>
        <div className="shadcn-command-input-wrap">
          <Search size={16} className="text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            className="shadcn-command-input"
            placeholder={placeholder}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
          />
          <button
            type="button"
            className="shadcn-dialog-close-btn"
            onClick={() => onOpenChange(false)}
            aria-label="Close command palette"
          >
            <X size={16} />
          </button>
        </div>

        <div className="shadcn-command-list">
          {filteredItems.length === 0 ? (
            <div style={{ padding: '1.5rem', textAlign: 'center', fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))' }}>
              No results found for &ldquo;{query}&rdquo;.
            </div>
          ) : (
            groups.map(([category, catItems]) => (
              <div key={category} style={{ marginBottom: '0.5rem' }}>
                <div className="shadcn-command-group-heading">{category}</div>
                {catItems.map((item) => {
                  const globalIdx = filteredItems.indexOf(item);
                  const isSelected = globalIdx === selectedIndex;
                  return (
                    <div
                      key={item.id}
                      className={`shadcn-command-item ${isSelected ? 'active' : ''}`}
                      onClick={() => {
                        item.onSelect();
                        onOpenChange(false);
                      }}
                      onMouseEnter={() => setSelectedIndex(globalIdx)}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                      {item.shortcut && (
                        <kbd className="shadcn-command-item-kbd">{item.shortcut}</kbd>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
